import {TTableHeaderStructure} from 'components/report-configuration/tabs/report-table-settings/modals/excel-header-structure-loader-modal/excel-header-structure-loader-modal.types';
import {IReportConfigurationAction} from 'features/configure-table-report-actions-menu';
import {ILookupEntry} from 'shared/types/lookups';

export type TReportConfigurationDatasourceType = 'SQL' | 'TABLE' | 'FUNCTION';
export enum EReportConfigurationPosition {
    TOP = 'TOP',
    BOTTOM = 'BOTTOM',
    TOP_AND_BOTTOM = 'TOP_AND_BOTTOM',
}
export type TReportConfigurationSortOrder = 'ASC' | 'DESC';
export type TReportConfigurationDocumentFormat = 'XLS' | 'XLSX' | 'DOC' | 'DOCX' | 'CSV' | 'TXT' | 'XML' | 'PDF';
export type TReportConfigurationTypeGroup = 'NUMBER' | 'DATE' | 'OTHER';
export type TReportConfigurationAlign = 'LEFT_TOP' | 'LEFT_MIDDLE' | 'LEFT_BOTTOM'
    | 'RIGHT_TOP' | 'RIGHT_MIDDLE' | 'RIGHT_BOTTOM' | 'MIDDLE_TOP' | 'MIDDLE_MIDDLE' | 'MIDDLE_BOTTOM';
export type TReportConfigurationFontStyle = 'FONT_NORMAL' | 'FONT_BOLD' | 'FONT_CURSIVE' | 'FONT_UNDERLINE'
    | 'FONT_BOLD_CURSIVE' | 'FONT_BOLD_UNDERLINE' | 'text-blue' | 'text-bold'
    | 'FONT_CURSIVE_UNDERLINE' | 'FONT_BOLD_UNDERLINE_CURSIVE' | 'FONT_MONOSPACE';
export type TReportConfigurationQueryParameterVariableType = 'STRING' | 'INTEGER' | 'NUMBER'
    | 'DATE' | 'DATETIME' | 'BOOLEAN' | 'VALUESET' | 'MULTIVALUESET' | 'SQLVALUESET' | 'SQLMULTIVALUESET'
    | 'VALUERANGE';
export type TReportConfigurationQueryParameterDefaultType = 'CONST' | 'CURRENT_DATETIME' | 'CONTEXT';

export interface IReportConfigurationCrColumnDto {
    columnName: string;
    sheetCode: string;
    sql: string;
}

export interface IReportConfigurationSortColumnDto {
    columnName: string;
    index: number;
    order: TReportConfigurationSortOrder;
}

export interface IReportConfigurationExportToDto {
    enabled: boolean;
    format: TReportConfigurationDocumentFormat;
    monitorProgramCode: string;
    monitorProgramId: number;
}

export interface IReportConfigurationDatasourceColumnDto {
    name: string;
    type: string;
    typeGroup: TReportConfigurationTypeGroup;
}

export interface IReportConfigurationReservedColumnsDto {
    pageNumber: string;
    rowNumber: string;
    sheetCode: string;
    rowIdColumnName: string;
    rowLinkColumnNames: string[];
}

export interface IReportConfigurationQueryParameterDto {
    columnName: string;
    defaultType: TReportConfigurationQueryParameterDefaultType;
    defaultValue: string;
    format: string;
    ordinal: number;
    paramName: string;
    reportTitle: string;
    required: boolean;
    sqlValueset: string;
    type: TReportConfigurationQueryParameterVariableType;
    valuesetLookupType: string;
    visible: boolean;
    periodLength?: number;
    inputRestrictions?: boolean;
}
interface IBlockedForRoleAndFunctionNames{
    roleNames: string[];
    functionName: string;
    description: string;
    conditions: ICustomColumnCondition[] | null;
}

export interface IReportConfigurationReportColumnDto {
    align: TReportConfigurationAlign;
    columnOrdinal: number;
    fieldType: string;
    typeGroup: string;
    format: string;
    hidden: boolean;
    reportTitle: string;
    style: TReportConfigurationFontStyle;
    blockedForRoleAndFunctionNames: IBlockedForRoleAndFunctionNames[];
    width: string;
    enabledTextWrapping: boolean;
    fieldFunction: 'DD' | 'URL' | 'ddUrl';
    closeFieldWhenChangingMode?: boolean;
}

interface ICustomColumnCondition {
    columnName: string;
    operator: string;
    value: string;
}

export interface ICustomColumnCase {
    color: string;
    icon: string;
    value: string;
    conditions: ICustomColumnCondition[] | null;
}

export type TCustomColumnType = 'ICON' | 'SWITCH';

export type TReportConfigurationCustomColumnDto = {
    columnName: string;
    type: TCustomColumnType;
    cases: ICustomColumnCase[];
} & Pick<IReportConfigurationReportColumnDto,
    'columnOrdinal'
    | 'hidden'
    | 'reportTitle'
    | 'align'
    | 'style'
    | 'enabledTextWrapping'
    | 'width'
    | 'fieldFunction'
    | 'closeFieldWhenChangingMode'>

export interface IReportConfigurationReportComparisionDto {
    attr: string[];
    key: string[];
    sheetCode: string;
}

export enum EColumnNamesFilterValues {
  LESS_ZERO = 'LESS_ZERO',
  GREATER_ZERO = 'GREATER_ZERO',
}

export interface IReportConfigurationReportGroupDto {
    aggFunction: 'MIN' | 'MAX' | 'SUM' | 'COUNT' | 'AVG';
    align: TReportConfigurationAlign;
    groupLabel: string;
    groupedColumnNames: string[];
    hiddenFlag: boolean;
    hierarchyLevel: number;
    placeholderColumnName: string;
    sortingDirection: TReportConfigurationSortOrder;
    style: TReportConfigurationFontStyle;
    subtotalLocation: EReportConfigurationPosition;
    groupedColumnNamesFilter?: EColumnNamesFilterValues | null;
}

export interface IReportConfigurationTableHeaderDto {
    block: number;
    hidden: boolean;
    ordinal: number;
    reportTitle: string;
}

export interface IReportConfigurationRowFormat {
    color: string;
    columns: string[];
    conditions: {
        columnName: string;
        operator: string;
        value: string;
    }[];
    filling: string;
    style: string;
}

export interface IReportConfigurationReportMenuItemDto {
    annexNumber: string;
    caption: string;
    children: IReportConfigurationReportMenuItemDto[];
    hidden: boolean;
    name: string;
    sheetCode: string;
    sheetNumber: string;
    reportColumns: {[reportColumnName: string]: IReportConfigurationReportColumnDto};
    excelTableHeaderStructure?: TReportConfigurationExcelHeaderTableStructureDto;
}

export type TReportConfigurationExcelHeaderTableStructureDto = TTableHeaderStructure;

export interface IReportConfigurationDataDto {
    createdBy: ILookupEntry;
    createdDate: string;
    updatedBy: ILookupEntry;
    updatedDate: string;
    templateCode: string;
    templateName: string;
    reportName: string;
    crColumns: IReportConfigurationCrColumnDto[];
    defaultSortColumns: IReportConfigurationSortColumnDto[];
    designTemplate: string;
    enabledColumnTitleOrdinals: boolean;
    enabledColumnsSettingsBtn: boolean;
    enabledComparison: boolean;
    enabledComparisonBtn: boolean;
    enabledCr: boolean;
    enabledCrBtn: boolean;
    enabledDownloadBtn: boolean;
    enabledAttachmentsBtn: boolean;
    enabledSelectAllRowsBtn: boolean;
    actionsEnabled: boolean;
    actions: IReportConfigurationAction[];
    enabledMenu: boolean;
    enabledRefreshBtn: boolean;
    enabledRowAttachments: boolean;
    enabledRowComments: boolean;
    enabledRowRequests: boolean;
    enabledSearch: boolean;
    enabledPrint: boolean;
    enabledViewControlRatioBtn: boolean;
    enabledDrillUpBtn: boolean;
    enabledEnabledPreExecution: boolean;
    enabledGroupingSettingsBtn: boolean;
    enabledRecordsInfo: boolean;
    enabledDetailedSummaryBtn?: boolean;
    enabledMenuWhenUsingDrillFilter?: boolean;
    excelTableHeaderStructure: TReportConfigurationExcelHeaderTableStructureDto;
    exportTo: IReportConfigurationExportToDto[];
    headerDatasourceColumns: IReportConfigurationDatasourceColumnDto[];
    headerDatasourceType: TReportConfigurationDatasourceType;
    headerDatasourceValue: string;
    headerDatasourceSqlValue: string;
    reservedColumns: IReportConfigurationReservedColumnsDto;
    paginationPosition: EReportConfigurationPosition;
    queryParameters: IReportConfigurationQueryParameterDto[];
    reportComparisons: IReportConfigurationReportComparisionDto[];
    reportColumns: {[reportColumnName: string]: IReportConfigurationReportColumnDto};
    customColumns: TReportConfigurationCustomColumnDto[];
    reportGroups: {[reportGroupName: string]: IReportConfigurationReportGroupDto};
    reportHeaders: {[reportHeaderName: string]: IReportConfigurationTableHeaderDto};
    reportMenu: IReportConfigurationReportMenuItemDto[];
    sheetCodeDatasourceColumnName: string;
    sheetCodeDatasourceType: TReportConfigurationDatasourceType;
    sheetCodeDatasourceValue: string;
    tableDatasourceColumns: IReportConfigurationDatasourceColumnDto[];
    tableDatasourceType:TReportConfigurationDatasourceType;
    tableDatasourceValue: string;
    tableDatasourceSqlValue: string;
    withDocumentId: boolean;
    rowFormats: IReportConfigurationRowFormat[];
    groupAfterUserFilter: boolean;
    // deprecated ↴
    useHeader?: boolean;
}
